import { useMemo } from 'react'

import { OfframpDisplayData } from '~/components/txn-display/state-change/types'
import { BRIDGE_FEE_PERCENT, getBridgeTokenType } from '~/constants/addresses'
import { useActiveOrg } from '~/hooks/org'
import { TokenMetadata } from '~/hooks/useTokenMetadata'
import { IAddress } from '~/types'
import { Address } from '~/utils/address'

// The transfer is an offramp when the receiver is a bank's liquidation address.
export const findBankAccountMatch = <
  T extends { liquidationAddress: IAddress },
>(
  externalAccounts: T[] | undefined,
  receiver: IAddress | undefined,
): T | undefined =>
  receiver
    ? externalAccounts?.find(({ liquidationAddress }) =>
        Address.equal(receiver, liquidationAddress),
      )
    : undefined

export const useBankAccountMatch = (receiver: IAddress | undefined) => {
  const { org } = useActiveOrg()
  const externalAccounts = org?.bridge?.externalAccounts

  return useMemo(
    () => findBankAccountMatch(externalAccounts, receiver),
    [externalAccounts, receiver],
  )
}

// Current Bridge rate; historical transactions carry their own feePercent.
export const computeOfframpFee = (grossAmount: number) => ({
  processingFee: grossAmount * (BRIDGE_FEE_PERCENT / 100),
  processingFeeRate: BRIDGE_FEE_PERCENT / 100,
})

export const useOfframpDisplayData = ({
  enabled,
  receiver,
  transferToken,
  transferTokenMetadata,
}: {
  enabled: boolean
  receiver: IAddress | undefined
  transferToken: { amount?: string } | undefined
  transferTokenMetadata: TokenMetadata | undefined
}): OfframpDisplayData | undefined => {
  const bankAccountMatch = useBankAccountMatch(receiver)

  return useMemo(() => {
    if (!enabled || !bankAccountMatch) return undefined

    if (!transferToken?.amount || !transferTokenMetadata) return undefined

    // Use token amount directly (not USD value) so EUR offramps show fee in EUR
    const tokenAmount = Number(transferToken.amount)

    // The bank's settlement token defines the currency — transferToken only
    // syncs to it in an effect after selection.
    const currency =
      getBridgeTokenType(
        bankAccountMatch.liquidationChainId,
        Address.format(bankAccountMatch.liquidationToken),
      ) === 'eurc'
        ? 'EUR'
        : 'USD'

    return {
      bankName: bankAccountMatch.name,
      liquidationAddress: bankAccountMatch.liquidationAddress,
      ...computeOfframpFee(tokenAmount),
      currency,
    }
  }, [enabled, bankAccountMatch, transferToken, transferTokenMetadata])
}
